import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { CLUSTER_NAME } from 'utils/constants'
import { milliseconds } from 'date-fns'
import { toContextTuplesString } from 'utils/logging'

export const SANCTIONED_COUNTRIES = {
  AG: 'Antigua and Barbuda',
  DZ: 'Algeria',
  BD: 'Bangladesh',
  BO: 'Bolivia',
  BY: 'Belarus',
  BI: 'Burundi',
  MM: 'Burma (Myanmar)',
  CI: "Cote D'Ivoire (Ivory Coast)",
  CU: 'Cuba',
  CD: 'Democratic Republic of Congo',
  EC: 'Ecuador',
  GB: 'United Kingdom',
  IR: 'Iran',
  IQ: 'Iraq',
  LR: 'Liberia',
  LY: 'Libya',
  ML: 'Mali',
  MA: 'Morocco',
  NP: 'Nepal',
  KP: 'North Korea',
  SO: 'Somalia',
  SD: 'Sudan',
  SY: 'Syria',
  VE: 'Venezuela',
  YE: 'Yemen',
  ZW: 'Zimbabwe',
  US: 'United States',
} as const
type SanctionedCountryCode = keyof typeof SANCTIONED_COUNTRIES

const PERP_ALLOWED: string[] = []
const SPOT_ALLOWED: string[] = []
const SWAP_ALLOWED: string[] = []
const BORROW_LEND_ALLOWED: string[] = []
const SHOW_WARNING: string[] = []

const fetchIpGeolocation = async () => {
  try {
    const response = await fetch(
      'https://geolocation.cabana-exchange.workers.dev',
    )
    if (!response.ok) {
      console.error(
        `Failed to fetch IP geolocation: ${toContextTuplesString({
          status: response.status,
          response: await response.text(),
        })}`,
      )
      return ''
    }
    const parsedResponse = (await response.json()) as { country: string }
    return parsedResponse?.country || ''
  } catch (e) {
    console.error('Failed to fetch IP geolocation', e)
    return ''
  }
}

export default function useIpAddress() {
  const [ipAllowed, setIpAllowed] = useState(true)
  const [spotAllowed, setSpotAllowed] = useState(true)
  const [perpAllowed, setPerpAllowed] = useState(true)
  const [swapAllowed, setSwapAllowed] = useState(true)
  const [borrowLendAllowed, setBorrowLendAllowed] = useState(true)
  const [showWarning, setShowWarning] = useState(false)
  const [ipCountry, setIpCountry] = useState('')

  const { data: ipCountryCode, isInitialLoading } = useQuery<string, Error>({
    queryKey: ['ip-address'],
    queryFn: fetchIpGeolocation,
    cacheTime: milliseconds({ minutes: 15 }),
    staleTime: milliseconds({ minutes: 15 }),
    retry: 3,
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    if (ipCountryCode) {
      setIpCountry(ipCountryCode)
      setIpAllowed(
        !SANCTIONED_COUNTRIES[ipCountryCode as SanctionedCountryCode],
      )
      setSpotAllowed(SPOT_ALLOWED.includes(ipCountryCode))
      setPerpAllowed(PERP_ALLOWED.includes(ipCountryCode))
      setSwapAllowed(SWAP_ALLOWED.includes(ipCountryCode))
      setBorrowLendAllowed(BORROW_LEND_ALLOWED.includes(ipCountryCode))
      setShowWarning(SHOW_WARNING.includes(ipCountryCode))
    }
  }, [ipCountryCode])

  if (
    CLUSTER_NAME === 'mainnet-beta' &&
    !process.env.NEXT_PUBLIC_DISABLE_GEOBLOCK
  ) {
    return {
      ipAllowed,
      spotAllowed,
      perpAllowed,
      swapAllowed,
      borrowLendAllowed,
      showWarning,
      ipCountry,
      loadingIpCountry: isInitialLoading,
    }
  } else {
    return {
      ipAllowed: true,
      spotAllowed: true,
      perpAllowed: true,
      swapAllowed: true,
      borrowLendAllowed: true,
      showWarning: true,
      ipCountry,
      loadingIpCountry: isInitialLoading,
    }
  }
}
